import { useEffect, useState } from "react";
import { ApiError, readMyAuditorRecord, type AuditorRecord } from "../api/client";
import { Callout } from "../components/Callout";
import { AuditorOnboarding } from "./AuditorOnboarding";

/**
 * Screens 12–18, entered by way of what an auditor now actually has: one certification-body
 * relationship, or more than one (multiple-CBs-per-auditor).
 *
 * `GET .../me` is one record per membership.
 *
 * **Exactly one membership** — the wizard (or, once submitted, screen 18), unchanged: the
 * single-CB case this always was, delegated straight to `AuditorOnboarding`, which already
 * knows how to render either state for the one record it is given.
 *
 * **More than one** — an overview this screen owns. Education and experience are asked once
 * and are the same answer for every relationship (`auditor_profiles` is shared, not
 * tenant-scoped), so they are shown once, above the per-CB sections below them. Each
 * certification body's own competence, declarations and excluded clients are its own
 * determination — one CB cannot see what another was told — so those stay in that CB's own
 * card, exactly as they are on the server.
 *
 * A pending invitation's "Complete" button opens the wizard in place, scoped to that one
 * membership; leaving it returns to the overview and re-reads the list, so a record just
 * submitted shows as submitted rather than stale.
 */
export function MyCompetenceRecord() {
  const [records, setRecords] = useState<AuditorRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState<string | null>(null);

  const load = () => {
    readMyAuditorRecord()
      .then(setRecords)
      .catch((cause: unknown) =>
        setError(cause instanceof ApiError ? cause.message : "Your record could not be read."),
      );
  };

  useEffect(load, []);

  if (error) {
    return (
      <p className="problem" role="alert">
        {error}
      </p>
    );
  }

  if (records === null) {
    return <p className="sub">Reading your record&hellip;</p>;
  }

  const [only] = records;

  // The single-CB case, exactly as it always was — no overview in front of it.
  if (only && records.length === 1) {
    return <AuditorOnboarding membershipId={only.membership_id} />;
  }

  if (opened) {
    return (
      <>
        <button
          type="button"
          className="btn sec"
          style={{ marginBottom: "16px" }}
          onClick={() => {
            setOpened(null);
            setRecords(null);
            load();
          }}
        >
          &larr; My competence record
        </button>
        <AuditorOnboarding membershipId={opened} />
      </>
    );
  }

  if (!only) {
    return (
      <>
        <div className="eyebrow">Lead auditor</div>
        <h1>My competence record</h1>
        <p className="sub">
          No certification body has invited you yet. Once one does, complete your
          competence record here.
        </p>
      </>
    );
  }

  const shared = only;
  const pending = records.filter((entry) => entry.status === "invited");
  const held = records.filter((entry) => entry.status !== "invited");

  return (
    <>
      <div className="eyebrow">Lead auditor</div>
      <h1>My competence record</h1>
      <p className="sub" style={{ marginBottom: "18px" }}>
        Your qualifications, and your standing with each certification body you work with.
      </p>

      {pending.map((entry) => (
        <Callout
          key={entry.membership_id}
          tone="warn"
          title={`${entry.tenant_name ?? "A certification body"} has invited you.`}
        >
          Complete a competence record for {entry.tenant_name ?? "them"} before they can
          consider you for allocation — your standing with any other certification body is
          unaffected.
          <div style={{ marginTop: "10px" }}>
            <button
              type="button"
              className="btn sm"
              onClick={() => setOpened(entry.membership_id)}
            >
              Complete for {entry.tenant_name ?? "this certification body"}
            </button>
          </div>
        </Callout>
      ))}

      <div className="card">
        <h2>About you</h2>
        <p className="sub" style={{ marginBottom: "12px" }}>
          Shared across every certification body you work with — submitted once, not
          re-asked per invitation.
        </p>
        <dl className="kv">
          <dt>Name</dt>
          <dd>{shared.name}</dd>
          <dt>Education</dt>
          <dd>{shared.education || "—"}</dd>
          <dt>Audit experience</dt>
          <dd>
            {shared.audits_led ?? 0} audits led · {shared.audit_days ?? 0} audit days ·{" "}
            {shared.ms_years ?? 0} years in management systems
          </dd>
        </dl>
      </div>

      {held.length > 0 ? (
        <>
          <h2 style={{ margin: "26px 0 4px" }}>Certification bodies</h2>
          <p className="sub" style={{ marginBottom: "16px" }}>
            Competence, declarations and excluded clients are each body&rsquo;s own
            determination — one certification body cannot see what you told another.
          </p>
        </>
      ) : null}

      {held.map((entry) => (
        <MembershipCard key={entry.membership_id} record={entry} />
      ))}
    </>
  );
}

/** One certification body's own competence record — submitted, active or suspended. */
function MembershipCard({ record }: { record: AuditorRecord }) {
  const competence = record.competence ?? [];
  const exclusions = record.exclusions ?? [];
  const suspended = record.status === "suspended";

  return (
    <div className="card" style={{ opacity: suspended ? 0.72 : 1 }}>
      <div className="between" style={{ marginBottom: "10px" }}>
        <div>
          <h2>{record.tenant_name ?? "Certification body"}</h2>
          <p className="sub">
            {record.engagement ?? "—"}
            {record.invited_at ? ` · invited ${record.invited_at.slice(0, 10)}` : ""}
          </p>
        </div>
        <StatusPill status={record.status} />
      </div>
      <dl className="kv">
        <dt>Competence claimed</dt>
        <dd>
          {competence.length === 0
            ? "—"
            : competence.map((claim) => (
                <div key={claim.standard}>
                  <b>{claim.standard}</b>{" "}
                  <span className="sub">
                    as {claim.level.toLowerCase()}, {(claim.sectors ?? []).length}{" "}
                    {(claim.sectors ?? []).length === 1 ? "sector" : "sectors"}
                  </span>
                </div>
              ))}
        </dd>
        <dt>Declarations</dt>
        <dd>
          Confidentiality{" "}
          <span className={record.confidentiality ? "pill p-ok" : "pill"}>
            {record.confidentiality ? "Signed" : "—"}
          </span>
          &nbsp;Impartiality{" "}
          <span className={record.impartiality ? "pill p-ok" : "pill"}>
            {record.impartiality ? "Signed" : "—"}
          </span>
          &nbsp;AI accountability{" "}
          <span className={record.ai_accountability ? "pill p-ok" : "pill p-wait"}>
            {record.ai_accountability ? "Accepted" : "Not accepted"}
          </span>
        </dd>
        {record.witnessed_on ? (
          <>
            <dt>Witnessed audit</dt>
            <dd>
              Witnessed by {record.witnessed_by ?? "—"}, {record.witnessed_on}
            </dd>
          </>
        ) : null}
        <dt>Excluded clients</dt>
        <dd>
          <span className="sub">Visible only to {record.tenant_name} — </span>
          {exclusions.length === 0
            ? "None declared"
            : exclusions.map((entry) => (
                <div key={entry.organisation_name}>{entry.organisation_name}</div>
              ))}
        </dd>
      </dl>
    </div>
  );
}

function StatusPill({ status }: { status: string | null }) {
  if (status === "active") return <span className="pill p-ok">Active</span>;
  if (status === "suspended") return <span className="pill">Suspended</span>;
  if (status === "competence_expiring") {
    return <span className="pill p-wait">Competence expiring</span>;
  }
  return <span className="pill p-wait">Awaiting your certification body&rsquo;s verification</span>;
}
